// react and js
import PropTypes from "prop-types";
import * as Yup from "yup";
import { React, useState, useRef, useEffect, useContext } from "react";
import { useFormik, FormikProvider } from "formik";
import { useTranslation } from "react-i18next";

// mui
import {
  Box,
  Stack,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import backendRequest from "../../../components/BackendRequest";
import CountrySelect, { countries } from "../../../components/CountrySelect";

// hooks
import appContext from "../../../hooks/appContext";

// layouts

// pages

// theme
import {
  B1Small,
  ModalContentSwitch,
  ModalContentTextField,
  ModalActionsLoadingButton,
} from "../../../theme/styled";

// utils
import { combineReturnMessage, TranslatedMessage } from "../../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function RequestBackendAction(
  setSubmitting,
  setError,
  setSuccess,
  setWarning,
  setReturnMessage,
  postRequestUpdate,
  SubmitRequestContent
) {
  if (
    localStorage.getItem("myReauthentication") === "false" &&
    navigator.onLine === true
  ) {
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");

    backendRequest(
      process.env.REACT_APP_URL_API_DASHBOARD,
      SubmitRequestContent
    )
      .then((contentFromBackend) => {
        const keyOnly = Object.keys(contentFromBackend);
        const checkKey = keyOnly.includes("message");

        if (checkKey === true) {
          if (contentFromBackend.message === "success") {
            setSuccess(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
            postRequestUpdate(750);
          } else {
            setWarning(true);
            combineReturnMessage(contentFromBackend, setReturnMessage);
          }
        } else {
          setWarning(true);
          combineReturnMessage(contentFromBackend, setReturnMessage);
        }
        setSubmitting(false);
      })
      .catch(() => {
        if (navigator.onLine === true) {
          setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
          setError(true);
          setSubmitting(false);
        }
      });
  }
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandleDistributorPortalUpdateCustomersPersonalInformation({
  distributor,
  dashboardId,
  configDataClient,
  isBillingAddressRequiredProp,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandleDistributorPortalUpdateCustomersPersonalInformation.propTypes = {
    distributor: PropTypes.any.isRequired,
    dashboardId: PropTypes.any.isRequired,
    configDataClient: PropTypes.any.isRequired,
    isBillingAddressRequiredProp: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const wrapperRef = useRef(null);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const postRequestUpdate = (autoClose) => {
    setTimeout(() => {
      handleClose();
      setStateStorage((previousState) => ({
        ...previousState,
        widgetUpdateDistributorPortalManageDistributionCustomer: true,
        widgetUpdateDistributorPortalManageTickDataDistributionCustomer: true,
      }));
      setError(false);
      setSuccess(false);
      setWarning(false);
    }, autoClose);
  };

  let emailNotificationSettings = configDataClient.email_notification_settings;

  emailNotificationSettings = emailNotificationSettings.split(",");

  let newsletterInitValue = false;
  emailNotificationSettings.forEach((entry) => {
    if (entry.search("newsletter") !== -1) {
      const entrySplitted = entry.split("=");

      if (entrySplitted[1] === "1") {
        newsletterInitValue = true;
      }
    }
  });

  const [formMarketingShares, setFormMarketingShares] = useState(
    configDataClient.marketing_share === "1" ? Boolean(true) : Boolean(false)
  );
  const [formTailorizedAdds, setFormTailorizedAdds] = useState(
    configDataClient.tailorized_adds === "1" ? Boolean(true) : Boolean(false)
  );
  const [formEmailNewsletter, setFormEmailNewsletter] =
    useState(newsletterInitValue);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setError(false);
        setWarning(false);
        setSuccess(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, {
      passive: false,
      capture: false,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  let myValidationScheme = null;
  if (isBillingAddressRequiredProp === false) {
    myValidationScheme = Yup.object().shape({
      forename: Yup.string(),
      surname: Yup.string(),
      province: Yup.string(),
      city: Yup.string(),
      postal_code: Yup.string(),
      street: Yup.string(),
    });
  } else {
    myValidationScheme = Yup.object().shape({
      forename: Yup.string().required(t("distributorPortal.validation.forenameRequired")),
      surname: Yup.string().required(t("distributorPortal.validation.surnameRequired")),
      province: Yup.string(),
      city: Yup.string().required(t("distributorPortal.validation.cityRequired")),
      postal_code: Yup.string().required(t("distributorPortal.validation.postalCodeRequired")),
      street: Yup.string().required(t("distributorPortal.validation.streetRequired")),
    });
  }

  let initCountry = null;
  if (configDataClient.country != null) {
    countries.forEach((country) => {
      if (country.label === configDataClient.country) {
        initCountry = country;
      }
    });
  }

  const formik = useFormik({
    initialValues: {
      forename:
        configDataClient.forename !== null ? configDataClient.forename : "",
      surname:
        configDataClient.surname !== null ? configDataClient.surname : "",
      country: initCountry !== null ? initCountry.label : "",
      province:
        configDataClient.province !== null ? configDataClient.province : "",
      city: configDataClient.city !== null ? configDataClient.city : "",
      postal_code:
        configDataClient.postal_code !== null
          ? configDataClient.postal_code
          : "",
      street: configDataClient.street !== null ? configDataClient.street : "",
    },
    validationSchema: myValidationScheme,
    onSubmit: (values, { setSubmitting }) => {
      const SubmitRequestContent = {
        request_type: "dashboard",
        route_info: "distributor_portal",
        widget: "customer_and_account_management",
        operation: "update_customers_personal_information",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
        admin: {
          selected_distributor_id: distributor,
        },
        config_data_client: {
          selected_dashboard_id: dashboardId,
          forename: values.forename,
          surname: values.surname,
          marketing_share: formMarketingShares ? "1" : "0",
          tailorized_adds: formTailorizedAdds ? "1" : "0",
          email_notification_settings: `newsletter=${
            formEmailNewsletter ? "1" : "0"
          },`,
          country: values.country === null ? "" : values.country,
          province: values.province,
          city: values.city,
          postal_code: values.postal_code,
          street: values.street,
        },
      };

      RequestBackendAction(
        setSubmitting,
        setError,
        setSuccess,
        setWarning,
        setReturnMessage,
        postRequestUpdate,
        SubmitRequestContent
      );
    },
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);

    // Reinit form values when reopened
    emailNotificationSettings.forEach((entry) => {
      if (entry.search("newsletter") !== -1) {
        const entrySplitted = entry.split("=");

        if (entrySplitted[1] === "1") {
          setFormEmailNewsletter(true);
        } else {
          setFormEmailNewsletter(false);
        }
      }
    });

    if (configDataClient.marketing_share === "1") {
      setFormMarketingShares(true);
    } else {
      setFormMarketingShares(false);
    }

    if (configDataClient.tailorized_adds === "1") {
      setFormTailorizedAdds(true);
    } else {
      setFormTailorizedAdds(false);
    }

    formik.values.forename =
      configDataClient.forename !== null ? configDataClient.forename : "";
    formik.values.surname =
      configDataClient.surname !== null ? configDataClient.surname : "";
    formik.values.country = initCountry !== null ? initCountry.label : "";
    formik.values.province =
      configDataClient.province !== null ? configDataClient.province : "";
    formik.values.city =
      configDataClient.city !== null ? configDataClient.city : "";
    formik.values.postal_code =
      configDataClient.postal_code !== null ? configDataClient.postal_code : "";
    formik.values.street =
      configDataClient.street !== null ? configDataClient.street : "";
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  return ( stateStorage.userInformation.account_settings.config_data.config_data_distributor.manage_personal_information === "1" ?
    <>
      <B1Small
        fullWidth
        variant="contained"
        onClick={handleClickOpen("paper")}
        sx={{ mb: 2 }}
      >
        {t("distributorPortal.customer.actions.updatePersonalInfo")}
      </B1Small>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">
                {t("distributorPortal.customer.updatePersonalInfoDialog.title")}
              </Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                }}
              >
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.forename && errors.forename) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.forename && errors.forename) === true
                        ? t("distributorPortal.validation.forenameRequired")
                        : t("distributorPortal.customer.fields.forename")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("forename")}
                      error={Boolean(touched.forename && errors.forename)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.surname && errors.surname) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.surname && errors.surname) === true
                        ? t("distributorPortal.validation.surnameRequired")
                        : t("distributorPortal.customer.fields.surname")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("surname")}
                      error={Boolean(touched.surname && errors.surname)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.street && errors.street) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.street && errors.street) === true
                        ? t("distributorPortal.validation.streetRequired")
                        : t("distributorPortal.customer.fields.street")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("street")}
                      error={Boolean(touched.street && errors.street)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.postal_code && errors.postal_code) ===
                          true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.postal_code && errors.postal_code) ===
                      true
                        ? t("distributorPortal.validation.postalCodeRequired")
                        : t("distributorPortal.customer.fields.postalCode")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("postal_code")}
                      error={Boolean(touched.postal_code && errors.postal_code)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.city && errors.city) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.city && errors.city) === true
                        ? t("distributorPortal.validation.cityRequired")
                        : t("distributorPortal.customer.fields.city")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="contentSmallBold"
                      sx={{
                        color:
                          Boolean(touched.province && errors.province) === true
                            ? "red"
                            : "none",
                      }}
                    >
                      {Boolean(touched.province && errors.province) === true
                        ? t("distributorPortal.validation.provinceRequired")
                        : t("distributorPortal.customer.fields.provinceOptional")}
                    </Typography>
                    <ModalContentTextField
                      fullWidth
                      size="small"
                      type="text"
                      {...getFieldProps("province")}
                      error={Boolean(touched.province && errors.province)}
                    />
                  </Box>

                  <Box>
                    <Typography variant="contentSmallBold">{t("distributorPortal.customer.fields.country")}</Typography>
                    <CountrySelect
                      error={Boolean(touched.country && errors.country)}
                      initCountry={initCountry}
                      setFormCountry={(newValue) => {
                        values.country = newValue;
                      }}
                      nullable={!isBillingAddressRequiredProp}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      py: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.usePersonalDataMarketing")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formMarketingShares}
                      onChange={(event) => {
                        setFormMarketingShares(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      pb: 2,
                    }}
                  >
                    <Typography variant="contentSmall">
                      {t("distributorPortal.customer.fields.usePersonalDataTailorizedAdds")}
                    </Typography>
                    <ModalContentSwitch
                      checked={formTailorizedAdds}
                      onChange={(event) => {
                        setFormTailorizedAdds(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignContent: "center",
                      pb: 0,
                    }}
                  >
                    <Typography variant="contentSmall">{t("distributorPortal.customer.fields.newsletter")}</Typography>
                    <ModalContentSwitch
                      checked={formEmailNewsletter}
                      onChange={(event) => {
                        setFormEmailNewsletter(event.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <RequestMessage
                  wrapperRef={wrapperRef}
                  error={error}
                  warning={warning}
                  success={success}
                  returnMessage={returnMessage}
                />

                <ModalActionsLoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                >
                  {t("distributorPortal.common.update")}
                </ModalActionsLoadingButton>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </> : null
  );
}
